import React, {
	forwardRef,
	useCallback,
	useState,
	memo,
	useImperativeHandle,
	useRef,
} from 'react';

import {
	GoogleMap,
	useJsApiLoader,
	Marker,
	MarkerClusterer,
} from '@react-google-maps/api';

import { ContentWrapperStyled } from './InteractiveMap.styled';

// Components
import LoadingContainer from '../LoadingContainer/LoadingContainer';
import ErrorContainer from '../ErrorContainer/ErrorContainer';

// Initial settings and variables
import siteSettings from '../../siteSettings.json';
import colors from '../../theme/colors';

// Starting position of the map
const defaultMapSettings = { ...siteSettings.mapInitialValues };

const InteractiveMap = forwardRef((props, ref) => {
	// Get props
	const { locations, mapKey, isMobileLayout, setLocationHighlights } = props;

	// Reference to the map object so we can more easily control it
	const [currentMap, setMap] = useState(null);

	// Keep track of the map so we can determine its width and use this to add an offset to the camera
	const mapRef = useRef(null);

	// Map loader
	const { isLoaded, loadError } = useJsApiLoader({
		googleMapsApiKey: mapKey,
	});

	// Google maps options; please see https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions.rotateControl
	const mapOptions = {
		fullscreenControl: false,
		zoomControl: false,
		streetViewControl: false,
		mapTypeControl: false,
		keyboardShortcuts: false,
		minZoom: 2,
		maxZoom: 20,
	};

	const containerStyle = {
		width: '100%',
		height: '100%',
	};

	// Single marker: https://developers.google.com/maps/documentation/javascript/symbols / https://developers.google.com/maps/documentation/javascript/custom-markers
	const singleMarker = {
		url: './images/single-course-marker.svg',
		optimized: false,
	};

	// Multiple courses at location
	const singleMultipleMarker = {
		url: './images/multiple-course-marker.svg',
		optimized: false,
	};

	// https://googlemaps.github.io/v3-utility-library/interfaces/_google_markerclustererplus.clustericonstyle.html#textsize
	const clusterMarkers = [
		{
			url: './images/cluster-circle.svg',
			height: 32,
			width: 32,
			textSize: 16,
			textColor: colors.quinaryColor,
			fontFamily: 'degular',
		},
	];

	const onLoad = useCallback(function onLoad(mapInstance) {
		// Bind the map
		setMap(mapInstance);
	}, []);

	const onUnmount = useCallback(function callback(map) {
		// Unbind the map
		setMap(null);
	}, []);

	// So the parent can trigger function within the child
	useImperativeHandle(ref, () => ({
		setTarget(entry) {
			moveTo(entry);
		},
	}));

	const moveTo = (entry) => {
		// Center at location
		currentMap.panTo({
			lat: parseFloat(entry.lat),
			lng: parseFloat(entry.long),
		});

		if (!isMobileLayout) {
			// Add offset
			const offSetX = mapRef.current.getBoundingClientRect().width / 4;
			currentMap.panBy(-offSetX, 0);
		}
	};

	const onMarkerClick = (entry) => {
		// Set the location entry to push through to the sidepanel
		setLocationHighlights({ entry });
	};

	// Create the map
	const renderMap = () => {
		return (
			<ContentWrapperStyled ref={mapRef}>
				<GoogleMap
					mapContainerStyle={containerStyle}
					clickableIcons={false}
					options={mapOptions}
					onLoad={onLoad}
					onUnmount={onUnmount}
					center={defaultMapSettings.center}
					zoom={defaultMapSettings.zoom}
				>
					<MarkerClusterer
						averageCenter
						gridSize={50}
						zoomOnClick={true}
						styles={clusterMarkers}
					>
						{(clusterer) =>
							locations.map((entry, index) => (
								<Marker
									key={index}
									icon={entry.count > 1 ? singleMultipleMarker : singleMarker}
									position={{
										lat: parseFloat(entry.lat),
										lng: parseFloat(entry.long),
									}}
									clusterer={clusterer}
									onClick={() => {
										onMarkerClick(entry);
									}}
								/>
							))
						}
					</MarkerClusterer>
				</GoogleMap>
			</ContentWrapperStyled>
		);
	};

	// When an error occurred
	if (loadError) {
		return (
			<ContentWrapperStyled>
				<ErrorContainer />
			</ContentWrapperStyled>
		);
	}

	// Show either the map or the loading screen
	return isLoaded ? (
		renderMap()
	) : (
		<ContentWrapperStyled>
			<LoadingContainer />
		</ContentWrapperStyled>
	);
});

export default memo(InteractiveMap);
